import { calculateInvestmentProduct, formatRange, type InvestmentProduct, type PublicProductPrice } from './productInvestment';
/**
 * Wat is een offerte waard?
 *
 * Deze berekening stond eerder los in de publieke route, de PDF-service en de
 * Tiptap-PDF-blokken, met kleine verschillen. Eén definitie voorkomt dat het
 * bedrag in het overzicht afwijkt van het bedrag dat de klant op de PDF ziet.
 *
 * De bron is het offerteblok zelf, niet de productcatalogus: een offerteregel
 * is een momentopname en houdt zijn eigen bedrag, ook als het product later
 * verandert. Regels zonder productkoppeling tellen dus gewoon mee.
 */

export interface QuoteAmountRow {
  investmentProduct?: InvestmentProduct;
  productPrice?: PublicProductPrice;
  price?: number;
  hours?: number;
  subtotal?: number;
  /** Tekstregel zonder bedrag. */
  useText?: boolean;
  /** Handmatige tekst in de subtotaalcel van een uurtabel. */
  subtotalText?: string;
  optional?: boolean;
  selected?: boolean;
}

export interface QuoteAmountTable {
  pricingType?: string;
  fixedTotal?: number;
  rows?: QuoteAmountRow[];
}

export interface QuoteAmountBlock {
  blockType?: string;
  type?: string;
  content?: unknown;
}

export interface QuoteTotalOptions {
  /**
   * Optionele regels die de klant niet heeft aangevinkt buiten het bedrag laten.
   * Standaard aan: dat is ook wat de PDF en de publieke offerte optellen.
   */
  onlySelected?: boolean;
}

const number = (value: unknown) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

/**
 * Onder- en bovenkant van een bedrag. Een offerte met een van–tot prijs is nog een
 * concept; elk totaal daarvan is dan een bandbreedte. Zonder van–tot prijs zijn beide gelijk.
 */
export interface AmountRange {
  min: number;
  max: number;
}

const addRange = (sum: AmountRange, add: AmountRange): AmountRange => ({ min: sum.min + add.min, max: sum.max + add.max });
const single = (value: number): AmountRange => ({ min: value, max: value });

/** Bedrag van een productregel in hele euro's; zonder bedrag telt die als nul. Ook voor de editor. */
export function productRowRange(row: QuoteAmountRow): AmountRange | null {
  if (row.investmentProduct) {
    const computed = calculateInvestmentProduct(row.investmentProduct);
    return { min: (computed.minCents ?? 0) / 100, max: (computed.maxCents ?? 0) / 100 };
  }
  if (row.productPrice) {
    const min = (row.productPrice.totalCents ?? 0) / 100;
    return { min, max: row.productPrice.maxTotalCents == null ? min : row.productPrice.maxTotalCents / 100 };
  }
  return null;
}

/** Bandbreedte van één investeringstabel, in hele euro's zoals ze in het blok staan. */
export function investmentTableTotalRange(
  table: QuoteAmountTable,
  { onlySelected = true }: QuoteTotalOptions = {},
): AmountRange {
  if (table.pricingType === 'fixed') return (table.rows ?? []).reduce((sum, row) => {
    if (onlySelected && row.optional && !row.selected) return sum;
    return addRange(sum, productRowRange(row) ?? single(0));
  }, single(number(table.fixedTotal)));

  return (table.rows ?? []).reduce((sum, row) => {
    if (onlySelected && row.optional && !row.selected) return sum;
    const product = productRowRange(row);
    if (product) return addRange(sum, product);
    if (row.useText || (table.pricingType === 'hourly' && row.subtotalText)) return sum;
    if (table.pricingType === 'hourly') return addRange(sum, single(number(row.price) * number(row.hours)));
    return addRange(sum, single(number(row.subtotal)));
  }, single(0));
}

/** Ondergrens van één investeringstabel; zie investmentTableTotalRange voor de bandbreedte. */
export function investmentTableTotal(table: QuoteAmountTable, options: QuoteTotalOptions = {}): number {
  return investmentTableTotalRange(table, options).min;
}

/**
 * Totaal van een offerte uit de INVESTMENT- en PRICING-blokken.
 *
 * Blocktypes staan in de database zowel in hoofdletters als in kleine letters
 * (oudere offertes gebruiken kleine letters), dus we vergelijken hoofdletter-
 * ongevoelig — anders leest hetzelfde blok bij de ene offerte wel en bij de
 * andere niet.
 */
export function computeQuoteTotalRangeFromBlocks(
  blocks: QuoteAmountBlock[],
  options: QuoteTotalOptions = {},
): AmountRange {
  return (blocks ?? []).reduce((sum, block) => {
    const type = String(block.blockType || block.type || '').toUpperCase();
    const content = (block.content || {}) as Record<string, unknown>;

    if (type === 'PRICING') {
      const lineItems = content.lineItems as { total?: number }[] | undefined;
      // `!= null`: ook een opgeslagen `total: null` valt terug op de regels,
      // zoals de oude berekening en de editor (`??`) dat doen.
      const total =
        content.total != null
          ? content.total
          : lineItems?.reduce((lineSum, item) => lineSum + number(item.total), 0);
      return addRange(sum, single(number(total)));
    }

    if (type === 'INVESTMENT') {
      const tables = (content.tables as QuoteAmountTable[] | undefined) ?? [];
      return tables.reduce((tableSum, table) => addRange(tableSum, investmentTableTotalRange(table, options)), sum);
    }

    return sum;
  }, single(0));
}

/** Ondergrens van het offertetotaal; zie computeQuoteTotalRangeFromBlocks voor de bandbreedte. */
export function computeQuoteTotalFromBlocks(blocks: QuoteAmountBlock[], options: QuoteTotalOptions = {}): number {
  return computeQuoteTotalRangeFromBlocks(blocks, options).min;
}

/** Zelfde bedrag in centen, voor opslag en optellingen zonder afrondingsdrift. */
export function computeQuoteTotalCents(
  blocks: QuoteAmountBlock[],
  options: QuoteTotalOptions = {},
): number {
  return Math.round(computeQuoteTotalFromBlocks(blocks, options) * 100);
}

/** Bandbreedte in centen; minCents is gelijk aan computeQuoteTotalCents. */
export function computeQuoteTotalRangeCents(blocks: QuoteAmountBlock[], options: QuoteTotalOptions = {}) {
  const range = computeQuoteTotalRangeFromBlocks(blocks, options);
  return { minCents: Math.round(range.min * 100), maxCents: Math.round(range.max * 100) };
}

/** Totaal als tekst: "€ 6.125,00" of "€ 6.125,00 – € 8.000,00". */
export function formatAmountRange(range: AmountRange, format: (amount: number) => string): string {
  return formatRange(range.min, range.max, format);
}
